/**
 * NotifyDialog — admin→seller in-app notification composer (Track AD3, ADR-044).
 * `POST /admin/sellers/{seller_id}/notifications` (gated server-side on
 * `marketplace.seller.edit`). Body: `{ title, body, link_path? }` (`type`
 * defaults to `admin_direct` server-side; `source`/`language` are stamped
 * server-side and NOT accepted).
 *
 * 409 `notification.recipient_unclaimed` (the seller hasn't claimed a user
 * account) renders an inline explanation, NOT a generic error.
 */
import { useCallback, useEffect, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { BottomSheet, Button, Field } from "@garden/ui";
import { ApiError } from "@/lib/api-client";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { useNotifySeller } from "@/features/admin/sellers/hooks";

export interface NotifyDialogProps {
  open: boolean;
  sellerId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const TITLE_MAX = 200;
const BODY_MAX = 4000;
const LINK_MAX = 512;

export function NotifyDialog({
  open,
  sellerId,
  onClose,
  onSuccess,
}: NotifyDialogProps): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const { mutateAsync, isPending } = useNotifySeller();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [link, setLink] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setTitle("");
      setBody("");
      setLink("");
      setError(null);
    }
  }, [open]);

  const isValid =
    title.trim().length >= 1 &&
    title.length <= TITLE_MAX &&
    body.trim().length >= 1 &&
    body.length <= BODY_MAX &&
    link.length <= LINK_MAX;

  const handleSend = useCallback(async () => {
    if (!isValid) return;
    setError(null);
    try {
      await mutateAsync({
        id: sellerId,
        body: {
          title: title.trim(),
          body: body.trim(),
          link_path: link.trim() === "" ? null : link.trim(),
        },
      });
      onSuccess();
    } catch (err) {
      if (
        err instanceof ApiError &&
        err.problem?.code === "notification.recipient_unclaimed"
      ) {
        setError(t("sellers.notify.unclaimed"));
        return;
      }
      setError(t("error.generic"));
    }
  }, [isValid, mutateAsync, sellerId, title, body, link, onSuccess, t]);

  return (
    <BottomSheet open={open} onClose={onClose} title={t("sellers.notify.title")}>
      <div className="flex flex-col gap-4">
        <Field
          label={t("sellers.notify.titleLabel")}
          data-testid="notify-title-input"
          value={title}
          maxLength={TITLE_MAX}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t("sellers.notify.titlePlaceholder")}
        />

        <div className="flex flex-col gap-1.5">
          <label htmlFor="notify-body" className="text-sm font-medium">
            {t("sellers.notify.bodyLabel")}
          </label>
          <textarea
            id="notify-body"
            data-testid="notify-body-input"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={t("sellers.notify.bodyPlaceholder")}
            rows={4}
            maxLength={BODY_MAX}
            className="w-full resize-none rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]"
          />
        </div>

        <Field
          label={t("sellers.notify.linkLabel")}
          data-testid="notify-link-input"
          value={link}
          maxLength={LINK_MAX}
          onChange={(e) => setLink(e.target.value)}
          placeholder={t("sellers.notify.linkPlaceholder")}
        />

        {error ? (
          <p role="alert" data-testid="notify-error" className="text-sm text-[var(--g-error)]">
            {error}
          </p>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={isPending}
            className="min-h-[44px]"
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="primary"
            size="sm"
            data-testid="notify-send-btn"
            onClick={() => void handleSend()}
            disabled={!isValid}
            loading={isPending}
            className="min-h-[44px]"
          >
            {t("sellers.notify.send")}
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
}

export default NotifyDialog;
